import React from "react";
import {
  RouterProvider,
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  Navigate,
} from "react-router-dom";

import Login from "../pages/Login/Login";
import ErrorElement, {
  ErrorElementRoute,
} from "../components/ErrorElement/ErrorElement";
import AuthGard from "../components/Guards/AuthGuard";
import RouteGuard from "../components/Guards/RouteGuard";
import Layout from "../components/Layout/Layout";
import Podcast from "../pages/Podcast/Podcast";
import {
  listCategories,
  listPodcasts,
  listPromoCodes,
} from "../services/services";

const routeOption = { errorElement: <ErrorElementRoute /> };

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route path="/" errorElement={<ErrorElement />}>
      {/* Public Route */}
      <Route
        index
        element={
          <AuthGard>
            <Login />
          </AuthGard>
        }
        {...routeOption}
      />

      {/* Protected Layout Wrapper */}
      <Route
        element={
          <RouteGuard>
            <Layout />
          </RouteGuard>
        }
        {...routeOption}
      >
        {/* Dashboard */}
        <Route
          path="dashboard"
          lazy={async () => {
            const { default: Dashboard } = await import(
              "../pages/Dashboard/Dashboard"
            );
            return { Component: Dashboard };
          }}
          {...routeOption}
        />
        {/* Customers */}
        <Route
          path="customers"
          lazy={async () => {
            const { default: Users } = await import("../pages/Users/Users");
            return { Component: Users };
          }}
          {...routeOption}
        />
        <Route
          path="user-profile"
          lazy={async () => {
            const { default: UserProfile } = await import(
              "../pages/UserProfile/UserProfile"
            );
            return { Component: UserProfile };
          }}
          {...routeOption}
        />
        {/* Transactions / Transaction History */}
        <Route
          path="transactions"
          lazy={async () => {
            const { default: TransactionHistory } = await import(
              "../pages/Transactions/TransactionHistory"
            );
            return { Component: TransactionHistory };
          }}
          {...routeOption}
        />
        <Route
          path="transaction-history"
          lazy={async () => {
            const { default: TransactionHistory } = await import(
              "../pages/Transactions/TransactionHistory"
            );
            return { Component: TransactionHistory };
          }}
          {...routeOption}
        />
        {/* Onboarding */}
        <Route
          path="onboarding"
          lazy={async () => {
            const { default: OnboardingManager } = await import(
              "../pages/Onboarding/OnboardingManager"
            );
            return { Component: OnboardingManager };
          }}
          {...routeOption}
        />
        {/* Categories */}
        <Route
          path="categories"
          lazy={async () => {
            const { default: CategoriesList } = await import(
              "../pages/Categories/CategoriesList"
            );
            return { Component: CategoriesList };
          }}
          {...routeOption}
        />

        <Route
          path="questions"
          lazy={async () => {
            const { default: QuestionSets } = await import(
              "../pages/Questions/QuestionSets"
            );
            return { Component: QuestionSets };
          }}
          {...routeOption}
        />
        <Route
          path="questions/:categoryId"
          lazy={async () => {
            const { default: QuestionSets } = await import(
              "../pages/Questions/QuestionSets"
            );
            return { Component: QuestionSets };
          }}
          {...routeOption}
        />

        <Route
          path="Programs"
          lazy={async () => {
            const { default: ProgramList } = await import(
              "../pages/Programs/ProgramList"
            );
            return { Component: ProgramList };
          }}
          {...routeOption}
        />
        <Route
          path="MiniRetreat"
          lazy={async () => {
            const { default: MiniRetreat } = await import(
              "../pages/MiniRetreat/MiniRetreat"
            );
            return { Component: MiniRetreat };
          }}
          {...routeOption}
        />
        <Route
          path="Podcast"
          lazy={async () => {
            const { default: Podcast } = await import(
              "../pages/Podcast/Podcast"
            );
            return { Component: Podcast };
          }}
          {...routeOption}
        />
        <Route
          path="MeditationGuides"
          lazy={async () => {
            const { default: MeditationGuides } = await import(
              "../pages/MeditationGuides/MeditationGuides"
            );
            return { Component: MeditationGuides };
          }}
          {...routeOption}
        />
        <Route
          path="Magazines"
          lazy={async () => {
            const { default: Magazines } = await import(
              "../pages/Magazines/Magazines"
            );
            return { Component: Magazines };
          }}
          {...routeOption}
        />

        <Route
          path="ProgramDays/:programId"
          lazy={async () => {
            const { default: ProgramDays } = await import(
              "../pages/ProgramDays/ProgramDays"
            );
            return { Component: ProgramDays };
          }}
          {...routeOption}
        />
        <Route
          path="Activities/:programId"
          lazy={async () => {
            const { default: ActivityList } = await import(
              "../pages/Activities/ActivityList"
            );
            return { Component: ActivityList };
          }}
          {...routeOption}
        />
        <Route
          path="Banners"
          lazy={async () => {
            const { default: Banners } = await import(
              "../pages/Banners/Banners"
            );
            return { Component: Banners };
          }}
          {...routeOption}
        />
        <Route
          path="Campaign"
          lazy={async () => {
            const { default: Campaign } = await import(
              "../pages/Campaign/Campaign"
            );
            return { Component: Campaign };
          }}
          {...routeOption}
        />

        {/* Content */}
        <Route
          path="serenipost"
          lazy={async () => {
            const { default: SereniPost } = await import(
              "../pages/Content/SereniPost"
            );
            return { Component: SereniPost };
          }}
          {...routeOption}
        />
        {/* Stories */}
        <Route
          path="Stories/:name"
          lazy={async () => {
            const { default: Stories } = await import(
              "../pages/Stories/Stories"
            );
            return { Component: Stories };
          }}
          {...routeOption}
        />
        <Route
          path="stories/:name"
          lazy={async () => {
            const { default: Stories } = await import(
              "../pages/Stories/Stories"
            );
            return { Component: Stories };
          }}
          {...routeOption}
        />
        <Route path="Stories" element={<Navigate to="/Stories/Lara" replace />} />
        <Route path="stories" element={<Navigate to="/Stories/Lara" replace />} />

        <Route
          path="retreats"
          lazy={async () => {
            const { default: Retreats } = await import(
              "../pages/Content/Retreats"
            );
            return { Component: Retreats };
          }}
          {...routeOption}
        />
        <Route
          path="show-history"
          lazy={async () => {
            const { default: ShowHistory } = await import(
              "../pages/Content/ShowHistory"
            );
            return { Component: ShowHistory };
          }}
          {...routeOption}
        />

        <Route
          path="promo-codes"
          lazy={async () => {
            const { default: PromoCodes } = await import(
              "../pages/PromoCodes/PromoCodes"
            );
            return { Component: PromoCodes };
          }}
          {...routeOption}
        />
        {/* Settings */}
        <Route
          path="version-control"
          lazy={async () => {
            const { default: VersionControl } = await import(
              "../pages/Settings/VersionControl"
            );
            return { Component: VersionControl };
          }}
          {...routeOption}
        />
        <Route
          path="feature-toggles"
          lazy={async () => {
            const { default: FeatureToggles } = await import(
              "../pages/Settings/FeatureToggles"
            );
            return { Component: FeatureToggles };
          }}
          {...routeOption}
        />
        <Route
          path="About"
          lazy={async () => {
            const { default: About } = await import("../pages/About/About");
            return { Component: About };
          }}
          {...routeOption}
        />
        <Route
          path="contact-us"
          lazy={async () => {
            const { default: ContactUsList } = await import(
              "../pages/ContactUs/ContactUsList"
            );
            return { Component: ContactUsList };
          }}
          {...routeOption}
        />
      </Route>
    </Route>
  )
);

function Routing() {
  return <RouterProvider router={router} />;
}

export default Routing;
